/**
 * Tasks.tsx — lista kartoteki „Zadania" (faza 30, ADR-0123), tenant scope.
 *
 * Lista na silniku list (`GET /api/tasks`), kolumny data-driven z katalogu pól modułu `tasks`
 * (`buildCoreColumns`) + nadpisania dla statusu, priorytetu i odwołań bez celu w katalogu
 * (`taskListColumns.tsx`). Wzorzec: `Employees.tsx`.
 *
 *  - „Dodaj" otwiera okno wyboru typu zadania (typ wyznacza firmę i numerację), a potem formularz
 *    `/tasks/new?taskTypeId=…`.
 *  - Aktywna firma bez typów zadań → komunikat „najpierw dodaj typ zadania" z odnośnikiem do słownika.
 *  - Tryby widoku (lista / karty) jak w innych kartotekach; Kanban i kalendarz pojawiają się tylko
 *    wtedy, gdy układ listy ma ich konfigurację. Przeciągnięcie karty na tablicy Kanban to zwykły
 *    zapis zadania jak w formularzu (`useTaskKanbanMove`): zapisuje pole, po którym tablica
 *    grupuje (status, priorytet — pola wyboru); przy innym polu grupowania albo bez prawa edycji
 *    zadań tablica jest tylko do podglądu.
 *  - Akcje listy (eksport, zmiana zbiorcza, usuń…) przez wspólny `useListActions`; import — jak u
 *    pracowników — nie ma handlera strony („wkrótce").
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { useServerListControls } from '@/components/configurable-table/useServerListControls';
import { Can } from '@/components/permissions/PermissionGate';
import {
  orderTaskCoreColumns,
  TASK_COLUMN_ID_TO_SLUG,
  TASK_CORE_OVERRIDES,
  TASK_EXCLUDE_SLUGS,
  TASK_PROCESS_LABEL_PREFIX,
} from '@/components/tasks/taskListColumns';
import { TaskTypePickerModal } from '@/components/tasks/TaskTypePickerModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildProcessColumns } from '@/lib/documents/process-columns';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import {
  buildTasksListParams,
  TASK_TYPES_PERMISSION,
  type TaskListItem,
  TASKS_QUERY_ROOT,
  type TasksListFilters,
  useTasksList,
  useTaskTypes,
} from '@/lib/tasks/task-api';
import { useTaskKanbanMove } from '@/lib/tasks/useTaskKanbanMove';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const MODULE_SLUG = 'tasks';

export function Tasks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const withMenuItem = useWithMenuItem();
  const { me } = useTenantAuth();
  const viewModes = useListViewMode();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Aktywna firma (przełącznik w topbarze) zawęża listę po stronie serwera; „Wszystkie firmy" = unia.
  const companyScope = useActiveCompanyScope();
  const companyId = companyIdParam(companyScope);

  // Układ listy wskazany z pozycji menu (jeden `tableId` dla widżetów, filtrów i tabeli).
  const forcedListLayoutId = useForcedListLayoutId(MODULE_SLUG);
  const tableId = forcedListLayoutId ? `${MODULE_SLUG}::layout-${forcedListLayoutId}` : MODULE_SLUG;
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  const meCompanies = useMemo(() => me?.companies ?? [], [me?.companies]);

  // Typy zadań w zakresie firmy listy — brak jakiegokolwiek typu = stan pusty „najpierw dodaj typ".
  const typesQ = useTaskTypes({ companyId });
  const hasNoTypes = typesQ.isSuccess && typesQ.data.length === 0;

  const fieldsQ = useSystemModuleFields(MODULE_SLUG);
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  const extColumns = useMemo<ColumnDefinition<TaskListItem>[]>(
    () => buildExtColumns<TaskListItem>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana zakresu firmy zawęża wynik — wróć na pierwszą stronę.
  useEffect(() => {
    controls.resetPage();
  }, [companyId, controls.resetPage]);

  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  // Moduł procesów aktywny → kolumny przekrojowe (aktywny proces / bieżący etap) domyślnie widoczne
  // (jak na listach dokumentów); nieaktywny → domyślnie ukryte (serwer i tak nie dokłada danych).
  const isProcessModuleActive = useMemo(
    () => me?.modules?.some((m) => m.code === 'processes') ?? false,
    [me?.modules],
  );

  const buildTaskCoreColumns = useCallback(
    (getLabels: () => ExtRefLabels | undefined): ColumnDefinition<TaskListItem>[] =>
      buildCoreColumns<TaskListItem>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels: getLabels,
        overrides: TASK_CORE_OVERRIDES,
        excludeSlugs: TASK_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
    ],
  );
  const coreColumns = useMemo(
    () => buildTaskCoreColumns(getRefLabels),
    [buildTaskCoreColumns, getRefLabels],
  );

  // Kolumny przekrojowe procesu (poza katalogiem pól) — wspólna fabryka z listami dokumentów.
  const processColumns = useMemo<ColumnDefinition<TaskListItem>[]>(
    () =>
      buildProcessColumns<TaskListItem>({
        t,
        labelKeyPrefix: TASK_PROCESS_LABEL_PREFIX,
        isSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        isProcessModuleActive,
      }),
    [t, controls.isCoreColumnSortable, sortableSlugs, isProcessModuleActive],
  );

  // Kolejność: znane kolumny rdzenne (jawnie), pozostałe rdzenne, firma, kategoria, proces, pola
  // dodatkowe.
  const assembleColumns = useCallback(
    (
      core: ColumnDefinition<TaskListItem>[],
      ext: ColumnDefinition<TaskListItem>[],
      getLabels: () => ExtRefLabels | undefined,
    ): ColumnDefinition<TaskListItem>[] => [
      ...orderTaskCoreColumns(core),
      buildCompanyColumn<TaskListItem>({ companies: meCompanies, getRefLabels: getLabels, t }),
      buildCategoryColumn<TaskListItem>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      ...processColumns,
      ...ext,
    ],
    [meCompanies, t, controls.isCoreColumnSortable, sortableSlugs, processColumns],
  );
  const columns = useMemo(
    () => assembleColumns(coreColumns, extColumns, getRefLabels),
    [assembleColumns, coreColumns, extColumns, getRefLabels],
  );

  // id kolumny → slug backendu: rdzenne mają id = slug, firma, kategoria i proces — jawnie.
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...TASK_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id;
    return map;
  }, [coreColumns]);

  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns: listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
    ],
  );

  // Filtry do serwera jako jedna tablica `headerFilters`: popup nagłówka + wiersz „Szukaj…" +
  // aktywny szybki filtr.
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];
    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }
    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const fieldSlug = columns.find((c) => c.id === columnId)?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const fieldSlug = columns.find((c) => c.id === cond.field)?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }
    return merged;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);
  const serializedHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const listHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : serializedHeaderFilters;

  // Filtry widoku bez stronicowania i projekcji — jedno źródło dla zapytania listy i eksportu CSV.
  const listFilters = useMemo<TasksListFilters>(
    () => ({
      companyId,
      search: debouncedSearch || undefined,
      sort: controls.queryParams.sort,
      order: controls.queryParams.order,
      headerFilters: serializedHeaderFilters,
    }),
    [
      companyId,
      debouncedSearch,
      controls.queryParams.sort,
      controls.queryParams.order,
      serializedHeaderFilters,
    ],
  );
  const tasksQ = useTasksList({
    ...listFilters,
    headerFilters: listHeaderFilters,
    limit: controls.queryParams.limit,
    offset: controls.queryParams.offset,
    columns: visibleColumnParams.length > 0 ? visibleColumnParams.join(',') : undefined,
  });
  setRefLabels(tasksQ.data?.extRefLabels);
  useEffect(() => {
    setSortableSlugs(tasksQ.data?.meta?.sortable);
  }, [tasksQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(tasksQ.data?.meta?.filterable);
  }, [tasksQ.data?.meta?.filterable]);
  const tasks = tasksQ.data?.tasks ?? [];

  // Drag & drop na tablicy Kanban = zwykły zapis zadania na zasadach formularza (pole grupowania
  // tablicy, komunikaty, bramka prawa edycji) — patrz hak.
  const kanbanMove = useTaskKanbanMove({
    fields: fieldsQ.data ?? [],
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_ROOT] });
      void queryClient.invalidateQueries({ queryKey: ['distinct-values', MODULE_SLUG] });
    },
  });

  // Eksport CSV — dokładnie to, co lista (te same filtry, widoczne kolumny, ich kolejność).
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);
  const listExport = useEngineListExport<TaskListItem>({
    target: 'tasks',
    filename: 'zadania',
    buildFilterParams: () => new URLSearchParams(buildTasksListParams(listFilters)),
    columns,
    visibleColumnIds,
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return assembleColumns(
        buildTaskCoreColumns(getLabels),
        buildExtColumns<TaskListItem>(fieldsQ.data ?? [], t, getLabels),
        getLabels,
      );
    },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.tasks.list.title'),
  });

  const taskActions = useListActions({
    kartotekaKey: MODULE_SLUG,
    entity: MODULE_SLUG,
    queryKey: [TASKS_QUERY_ROOT, 'list'],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: { ...listExport.handlers },
    testIdPrefix: 'tasks',
  });

  return (
    <main className="flex h-full min-h-0 flex-col bg-bg-secondary p-6" data-testid="tasks-page">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text-primary">{t('tenant.tasks.list.title')}</h1>
            <p className="mt-1 text-sm text-text-tertiary">{t('tenant.tasks.list.subtitle')}</p>
          </div>
          {!hasNoTypes && (
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="tasks" />
              <ListActionsMenu {...taskActions.menuProps} />
              <ListBuilderMenu slug={MODULE_SLUG} testIdPrefix="tasks" />
              <Button
                variant="primary"
                size="sm"
                onClick={() => setPickerOpen(true)}
                leftIcon={<Plus size={16} aria-hidden="true" />}
                data-testid="tasks-add-btn"
              >
                {t('tenant.tasks.list.addBtn')}
              </Button>
            </div>
          )}
        </header>

        {hasNoTypes ? (
          <Card
            className="flex flex-col items-center gap-3 py-12 text-center"
            data-testid="tasks-empty-types"
          >
            <ClipboardList className="h-10 w-10 text-text-muted" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-text-primary">
              {t('tenant.tasks.list.emptyTypesTitle')}
            </h2>
            <p className="max-w-md text-sm text-text-tertiary">
              {t('tenant.tasks.list.emptyTypesDescription')}
            </p>
            <Can
              code={TASK_TYPES_PERMISSION.view}
              fallback={
                <p
                  className="max-w-md text-sm text-text-secondary"
                  data-testid="tasks-empty-types-ask-admin"
                >
                  {t('tenant.tasks.list.emptyTypesAskAdmin')}
                </p>
              }
            >
              <Button
                variant="primary"
                onClick={() => navigate('/settings/task-types')}
                data-testid="tasks-empty-types-action"
              >
                {t('tenant.tasks.list.emptyTypesAction')}
              </Button>
            </Can>
          </Card>
        ) : (
          <>
            <div className="shrink-0">
              <ListTopWidgets tableId={tableId} categoryId={null} companyId={companyId} />
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {fieldsQ.isError ? (
                <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
              ) : (
                <ConfigurableTable<TaskListItem>
                  tableId={tableId}
                  testIdPrefix="tasks"
                  showOrdinal
                  fillHeight
                  listToolbar={
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <ListFilterBar
                          tableId={tableId}
                          columns={columns}
                          search={search}
                          onSearchChange={(next) => {
                            setSearch(next);
                            controls.resetPage();
                          }}
                          searchPlaceholder={t('tenant.tasks.list.searchPlaceholder')}
                          headerFilters={headerFilters}
                          onHeaderFiltersChange={handleHeaderFiltersChange}
                          onRefresh={() =>
                            void queryClient.invalidateQueries({
                              queryKey: [TASKS_QUERY_ROOT, 'list'],
                            })
                          }
                          testIdPrefix="tasks"
                        />
                      </div>
                      <QuickFilterBar
                        quickFilters={quickFilters}
                        activeId={activeQuickFilterId}
                        onChange={(id) => {
                          setActiveQuickFilterId(id);
                          controls.resetPage();
                        }}
                      />
                    </div>
                  }
                  {...viewModes.tableProps}
                  columns={columns}
                  data={tasks}
                  isLoading={tasksQ.isLoading}
                  loadError={tasksQ.error}
                  onRetryLoad={() => void tasksQ.refetch()}
                  getRowId={(row) => row.id}
                  selectable
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  onVisibleColumnsChange={setVisibleColumnIds}
                  onRowContextMenu={taskActions.onRowContextMenu}
                  headerFilters={headerFilters}
                  onHeaderFiltersChange={handleHeaderFiltersChange}
                  filterable
                  columnFilters={columnFilters}
                  onColumnFiltersChange={(next) => {
                    setColumnFilters(next);
                    controls.resetPage();
                  }}
                  {...controls.tableProps}
                  totalItems={tasksQ.data?.total ?? 0}
                  isFetching={tasksQ.isFetching}
                  onCalendarRangeChange={controls.setCalendarRange}
                  onRowClick={(row) => navigate(withMenuItem(`/tasks/${row.id}`))}
                  onRowUpdate={kanbanMove.onRowUpdate}
                  canMoveKanbanField={kanbanMove.canMoveField}
                  emptyIcon={ClipboardList}
                  emptyTitle={t('tenant.tasks.list.empty')}
                />
              )}
            </div>
          </>
        )}
      </div>

      <TaskTypePickerModal
        open={pickerOpen}
        preferredCompanyId={companyId ?? null}
        onClose={() => setPickerOpen(false)}
        onSelect={(taskTypeId) => {
          setPickerOpen(false);
          navigate(withMenuItem(`/tasks/new?taskTypeId=${encodeURIComponent(taskTypeId)}`));
        }}
      />

      {kanbanMove.dialog}

      {taskActions.overlays}
    </main>
  );
}
